import React, { useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@wix/da-shared-react/pkg/Icons/24x24/Search';
import CloseBold from '@wix/da-shared-react/pkg/Icons/CloseBold';
import IconButton from '@wix/da-shared-react/pkg/Button/IconButton';
import SearchInputField from './SearchInputField';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiEventType } from '@wix/da-bi/pkg/constants';

import * as s from './SearchField.scss';

export interface Props {
  className?: string;
  searchInputValue: string;
  searchQuery: string;
  setSearchInputValue: (value: string) => void;
  setSearchQuery: (value: string) => void;
  clearSearch: () => void;
}

export const SearchField: React.FC<Props> = ({
  searchQuery,
  clearSearch,
  setSearchQuery,
  searchInputValue,
  className,
  setSearchInputValue,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const biLogger = useBiLogger();

  if (searchQuery && !isOpen) {
    setIsOpen(true);
  }

  const onSearchIconClick = () => {
    biLogger({
      evid: BiEventType.GALLERY_SEARCH_CLICK,
    });

    setIsOpen(true);
  };

  // Note: The close button below doesn't actually trigger this on pages without a searchQuery
  // because the onInputBlur function unmounts the button before it can launch this function.
  // We could use a ref to compare to the event, but the button below already has a BI related ref
  // passed to it inside the IconButton render function.
  const closeSearch = () => {
    clearSearch();
    setIsOpen(false);
  };

  const onInputBlur = () => {
    if (!searchQuery) {
      // Clear search here since anytime we close the search field we likely want it cleared.
      clearSearch();
      setIsOpen(false);
    }
  };

  const onInputEnter = () => {
    setSearchQuery(searchInputValue);
  };

  return (
    <div className={classnames(s['root'], isOpen && s['is-open'], className)}>
      {!isOpen && (
        <IconButton
          tooltipText={t('pages.gallections.search.searchAll')}
          onClick={onSearchIconClick}
        >
          <SearchIcon />
        </IconButton>
      )}
      {isOpen && (
        <>
          <SearchInputField
            value={searchInputValue}
            onChange={setSearchInputValue}
            onEnter={onInputEnter}
            onEscape={closeSearch}
            className={s['input']}
            onBlur={onInputBlur}
          />
          <IconButton className={s['close-button']} onClick={closeSearch}>
            <CloseBold />
          </IconButton>
        </>
      )}
    </div>
  );
};
SearchField.displayName = 'SearchField';

export default SearchField;
